import axios from 'axios';

// Create an axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  headers: {
    'Content-Type': 'application/json',
  },
  // Important for cookies to be sent with requests
  withCredentials: true,
});

// Add a request interceptor
api.interceptors.request.use(
  (config) => {
    // No need to manually add token - cookies are automatically sent
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized errors (token expired or invalid)
    if (error.response && error.response.status === 401) {
      // Only redirect to login for admin routes
      const currentPath = window.location.pathname;
      if (currentPath.startsWith('/admin') && currentPath !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (username: string, password: string) => 
    api.post('/auth/register', { username, password }),
  
  login: (username: string, password: string) => 
    api.post('/auth/login', { username, password }),
  
  logout: () => 
    api.post('/auth/logout'),
  
  getProfile: () => 
    api.get('/auth/profile'),
    
  getMe: () => 
    api.get('/auth/me'),
    
  checkAuth: () => 
    api.get('/auth/check'),
    
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
};

// Blog API
export const blogAPI = {
  getPosts: (page = 1, limit = 10, category?: string, tag?: string, search?: string, published?: boolean) => 
    api.get('/blog', { params: { page, limit, category, tag, search, published: published !== undefined ? String(published) : undefined } }),
  
  getPostBySlug: (slug: string) => 
    api.get(`/blog/${slug}`),
  
  createPost: (postData: any) => 
    api.post('/blog', postData),
  
  updatePost: (id: string, postData: any) => 
    api.put(`/blog/${id}`, postData),
  
  deletePost: (id: string) => 
    api.delete(`/blog/${id}`),
};

// Consultation API
export const consultationAPI = {
  createConsultation: (consultationData: any) => 
    api.post('/consultation', consultationData),
  
  getConsultations: (page = 1, limit = 10, status?: string) => 
    api.get('/consultation', { params: { page, limit, status } }),
  
  getConsultation: (id: string) => 
    api.get(`/consultation/${id}`),
  
  updateConsultation: (id: string, consultationData: any) => 
    api.put(`/consultation/${id}`, consultationData),
  
  deleteConsultation: (id: string) => 
    api.delete(`/consultation/${id}`),
};

// Quiz API
export const quizAPI = {
  getActiveQuiz: () => api.get('/quiz/active'),
  getQuizById: (id: string) => api.get(`/quiz/${id}`),
  submitQuiz: (id: string, answers: { questionId: string; selectedOptionId: string }[]) => 
    api.post(`/quiz/${id}/submit`, { answers }),
    getAllQuizzes: (page = 1, limit = 10, showAll = false) => 
    api.get(`/quiz?page=${page}&limit=${limit}&showAll=${showAll}`),
  createQuiz: (quizData: any) => api.post('/quiz', quizData),
  updateQuiz: (id: string, quizData: any) => api.put(`/quiz/${id}`, quizData),
  deleteQuiz: (id: string) => api.delete(`/quiz/${id}`),
};

// Newsletter API
export const newsletterAPI = {
  subscribe: (email: string, name?: string) => {
    return api.post('/newsletter/subscribe', { email, name });
  },
  unsubscribe: (email: string) => {
    return api.post('/newsletter/unsubscribe', { email });
  },
  getSubscribers: () => {
    return api.get('/newsletter/subscribers');
  },
  checkSubscription: (email: string) => {
    return api.get(`/newsletter/check-subscription?email=${email}`);
  },
  updateSubscriberStatus: (id: string, isActive: boolean) => {
    return api.put(`/newsletter/subscribers/${id}`, { isActive });
  },
  sendNewsletter: (subject: string, content: string, subscriberIds: string[]) => {
    return api.post('/newsletter/send', { subject, content, subscriberIds });
  }
};

// User API
export const userAPI = {
  getQuizAttempts: () => {
    return api.get('/user/quiz-attempts');
  },
  updateProfile: (data: {
    name?: string;
    email?: string;
    currentPassword?: string;
    newPassword?: string;
  }) => {
    return api.put('/user/profile', data);
  },
};

// Page Views API
export const pageViewsAPI = {
  // Get page views statistics
  getPageViews: async () => {
    try {
      return await api.get('/analytics/page-views');
    } catch (error: any) {
      // No longer using mock data, just throw the error
      throw error;
    }
  },
  
  // Get page views by date range
  getPageViewsByDateRange: async (startDate: string, endDate: string) => {
    try {
      return await api.get('/analytics/page-views', { params: { startDate, endDate } });
    } catch (error: any) {
      // No longer using mock data, just throw the error
      throw error;
    }
  },
  
  // Get page views by page
  getPageViewsByPage: async () => {
    try {
      return await api.get('/analytics/page-views/by-page');
    } catch (error: any) {
      // No longer using mock data, just throw the error
      throw error;
    }
  },
  
  // Record a page view
  recordPageView: async (path: string) => {
    try {
      return await api.post('/analytics/page-views', { path });
    } catch (error: any) {
      // No longer silently failing, throw the error
      throw error;
    }
  },
};

// Contact API
export const contactAPI = {
  sendContactForm: (name: string, email: string, subject: string, message: string) => {
    return api.post('/contact', { name, email, subject, message });
  }
};

export default api; 
